import {
	getPreferredSupportedLanguage,
	localizedUrl,
	normalizeLanguage,
	readStoredLanguage,
	shouldRedirectSavedSpanishHome,
	writeStoredLanguage,
} from "./marketing_language.mjs";
import {
	THEME_STORAGE_KEY,
	getThemeIconName,
	normalizeThemePreference,
	readStoredTheme,
	resolveTheme as resolveThemePreference,
	writeStoredTheme,
} from "./marketing_theme.mjs";
import { initBlogEngagement, updateBlogEngagementSession } from "./blog_engagement.mjs";
import { SESSION_STATE_URL, accountViewState, getSessionState, logout } from "./session_api.mjs";

const THEME_CONTROL_SELECTOR = "[data-vl-theme-control]";
const THEME_OPTION_SELECTOR = "[data-vl-theme-option]";
const THEME_TRIGGER_SELECTOR = "[data-vl-theme-trigger]";
const AUTH_CTA_SELECTOR = "[data-auth-cta]";
const ACCOUNT_CONTROL_SELECTOR = "[data-account-controls]";
const AUTOPLAY_VIDEO_SELECTOR = "[data-vs-autoplay]";
const LANGUAGE_CHOICE_SELECTOR = "[data-language-choice]";
const LANGUAGE_SUGGESTION_SELECTOR = "[data-language-suggestion]";
let themeMediaQuery;

function getSystemTheme() {
	return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function resolveTheme(preference) {
	return resolveThemePreference(preference, getSystemTheme());
}

function getThemeStorage() {
	try {
		return window.localStorage;
	} catch (error) {
		return null;
	}
}

function getStoredThemePreference() {
	return readStoredTheme(getThemeStorage());
}

function setStoredThemePreference(preference) {
	return writeStoredTheme(getThemeStorage(), preference);
}

function getThemePreferenceLabel(preference) {
	const option = document.querySelector(
		`${THEME_OPTION_SELECTOR}[data-vl-theme-option="${preference}"]`
	);
	return option?.textContent?.trim() || preference;
}

function updateThemeControls(preference) {
	const normalized = normalizeThemePreference(preference) || "auto";
	document.querySelectorAll(THEME_CONTROL_SELECTOR).forEach((control) => {
		control.querySelectorAll(THEME_OPTION_SELECTOR).forEach((button) => {
			const option = normalizeThemePreference(button.dataset.vlThemeOption);
			const isActive = option === normalized;
			button.classList.toggle("is-active", isActive);
			button.setAttribute("aria-pressed", isActive ? "true" : "false");
		});
	});

	const label = getThemePreferenceLabel(normalized);
	document.querySelectorAll(THEME_TRIGGER_SELECTOR).forEach((trigger) => {
		const labelPrefix = String(trigger.dataset.vlThemeLabel || "Appearance").trim();
		const accessibleLabel = `${labelPrefix}: ${label}`;
		trigger.setAttribute("aria-label", accessibleLabel);
		trigger.setAttribute("title", accessibleLabel);
		trigger.dataset.vlThemeIcon = getThemeIconName(normalized);
	});
}

function updateThemeFavicons(theme) {
	document.querySelectorAll("[data-vl-theme-favicon]").forEach((link) => {
		link.media = link.dataset.vlThemeFavicon === theme ? "all" : "not all";
	});
}

function applyThemePreference(preference) {
	const normalized = normalizeThemePreference(preference) || "auto";
	const theme = resolveTheme(normalized);
	const root = document.documentElement;

	root.classList.add("vs-js");
	root.setAttribute("data-theme", theme);
	root.setAttribute("data-theme-mode", theme);
	root.setAttribute("data-vl-theme-preference", normalized);
	root.style.colorScheme = theme;

	if (document.body) {
		document.body.setAttribute("data-vl-fleet-theme", theme);
		document.body.setAttribute("data-vl-theme-preference", normalized);
		document.body.style.colorScheme = theme;
	}

	updateThemeControls(normalized);
	updateThemeFavicons(theme);

	document.dispatchEvent(
		new CustomEvent("vl:fleet-theme-change", {
			detail: { preference: normalized, theme },
		})
	);

	return { preference: normalized, theme };
}

function bindThemeControl(control) {
	if (!control || control.dataset.vlThemeBound === "1") return;
	control.dataset.vlThemeBound = "1";
	control.addEventListener("click", (event) => {
		const button = event.target.closest(THEME_OPTION_SELECTOR);
		if (!button || !control.contains(button)) return;

		const preference = normalizeThemePreference(button.dataset.vlThemeOption);
		if (!preference) return;

		setStoredThemePreference(preference);
		applyThemePreference(preference);
		const menu = button.closest(".vs-theme-menu");
		if (menu) {
			menu.removeAttribute("open");
			menu.querySelector(":scope > summary")?.focus();
		}
	});
}

function initThemeControls(scope = document) {
	scope.querySelectorAll(THEME_CONTROL_SELECTOR).forEach(bindThemeControl);
	updateThemeControls(
		normalizeThemePreference(
			document.documentElement.getAttribute("data-vl-theme-preference")
		) ||
			getStoredThemePreference() ||
			"auto"
	);
}

function initThemeMediaListener() {
	if (themeMediaQuery || !window.matchMedia) return;
	themeMediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
	const onChange = () => {
		const preference =
			normalizeThemePreference(
				document.documentElement.getAttribute("data-vl-theme-preference")
			) ||
			getStoredThemePreference() ||
			"auto";
		if (preference === "auto") applyThemePreference(preference);
	};

	if (themeMediaQuery.addEventListener) {
		themeMediaQuery.addEventListener("change", onChange);
	} else if (themeMediaQuery.addListener) {
		themeMediaQuery.addListener(onChange);
	}
}

function initDisclosureBehavior() {
	document.addEventListener("click", (event) => {
		document
			.querySelectorAll(".vs-theme-menu[open], .vs-account-menu[open]")
			.forEach((menu) => {
				if (!menu.contains(event.target)) menu.removeAttribute("open");
			});
	});

	document.addEventListener("keydown", (event) => {
		if (event.key !== "Escape") return;
		const openDisclosure = document.querySelector(
			".vs-theme-menu[open], .vs-account-menu[open], .vs-mobile-nav[open]"
		);
		if (!openDisclosure) return;
		openDisclosure.removeAttribute("open");
		openDisclosure.querySelector(":scope > summary")?.focus();
	});
}

function initTheme() {
	applyThemePreference(getStoredThemePreference() || "auto");
	initThemeControls();
	initThemeMediaListener();
	initDisclosureBehavior();
}

function getLanguageStorage() {
	try {
		return window.localStorage;
	} catch (error) {
		return null;
	}
}

function getBrowserLanguages() {
	if (Array.isArray(navigator.languages) && navigator.languages.length) {
		return navigator.languages;
	}
	return navigator.language ? [navigator.language] : [];
}

function hideLanguageSuggestion(suggestion) {
	if (suggestion) suggestion.hidden = true;
}

function isPlainPrimaryClick(event, link) {
	return (
		!event.defaultPrevented &&
		(event.button === undefined || event.button === 0) &&
		!event.metaKey &&
		!event.ctrlKey &&
		!event.shiftKey &&
		!event.altKey &&
		!link.hasAttribute("download") &&
		(!link.target || link.target === "_self")
	);
}

function initLanguagePreference(scope = document) {
	const site = scope.querySelector?.(".vs-site") || document.querySelector(".vs-site");
	if (!site || site.dataset.languagePreferenceBound === "1") return;
	site.dataset.languagePreferenceBound = "1";

	const currentLanguage = normalizeLanguage(site.dataset.locale);
	const storage = getLanguageStorage();
	const storedLanguage = readStoredLanguage(storage);
	if (
		shouldRedirectSavedSpanishHome({
			currentLocale: currentLanguage,
			pathname: window.location.pathname,
			storedLanguage,
		})
	) {
		const alternateRoute = site.dataset.alternateRoute;
		if (alternateRoute) {
			window.location.replace(localizedUrl(alternateRoute, window.location));
			return;
		}
	}

	const browserLanguage = getPreferredSupportedLanguage(getBrowserLanguages());
	const suggestedLanguage =
		storedLanguage && storedLanguage !== currentLanguage
			? storedLanguage
			: !storedLanguage && browserLanguage !== currentLanguage
			? browserLanguage
			: "";
	const suggestion = site.querySelector(LANGUAGE_SUGGESTION_SELECTOR);

	if (
		suggestion &&
		suggestedLanguage &&
		normalizeLanguage(suggestion.dataset.languageTarget) === suggestedLanguage
	) {
		suggestion.hidden = false;
	}

	site.addEventListener("click", (event) => {
		const dismiss = event.target.closest("[data-language-suggestion-dismiss]");
		if (dismiss && site.contains(dismiss)) {
			writeStoredLanguage(storage, currentLanguage);
			hideLanguageSuggestion(suggestion);
			return;
		}

		const link = event.target.closest(LANGUAGE_CHOICE_SELECTOR);
		if (!link || !site.contains(link)) return;
		const targetLanguage = normalizeLanguage(link.dataset.languageChoice);
		if (!targetLanguage) return;

		writeStoredLanguage(storage, targetLanguage);
		if (!isPlainPrimaryClick(event, link)) return;

		event.preventDefault();
		window.location.assign(localizedUrl(link.href, window.location));
	});
}

function initAutoplayVideos(scope = document) {
	const videos = scope.querySelectorAll(AUTOPLAY_VIDEO_SELECTOR);
	if (!videos.length) return;

	const reducedMotionQuery = window.matchMedia?.("(prefers-reduced-motion: reduce)");
	const syncPlayback = () => {
		const shouldReduceMotion = reducedMotionQuery?.matches === true;
		videos.forEach((video) => {
			video.muted = true;
			video.autoplay = !shouldReduceMotion;
			if (shouldReduceMotion) {
				video.pause();
				return;
			}
			video.play()?.catch(() => {});
		});
	};

	syncPlayback();
	if (reducedMotionQuery?.addEventListener) {
		reducedMotionQuery.addEventListener("change", syncPlayback);
	} else if (reducedMotionQuery?.addListener) {
		reducedMotionQuery.addListener(syncPlayback);
	}
}

function isAllowedFleetPath(value) {
	return /^\/fleet(?:\/[a-z0-9-]+)*$/.test(String(value || "").trim());
}

function applyAuthenticatedCtas(scope = document) {
	scope.querySelectorAll(AUTH_CTA_SELECTOR).forEach((cta) => {
		if (!cta.dataset.guestLabel) cta.dataset.guestLabel = cta.textContent.trim();
		if (!cta.dataset.guestHref) cta.dataset.guestHref = cta.getAttribute("href") || "";
		if (!cta.dataset.guestCtaId) cta.dataset.guestCtaId = cta.dataset.ctaId || "";
		const label = String(cta.dataset.authLabel || "").trim();
		const href = String(cta.dataset.authHref || "").trim();
		const ctaId = String(cta.dataset.authCtaId || "").trim();
		if (!label || !isAllowedFleetPath(href)) return;

		cta.textContent = label;
		cta.setAttribute("href", href);
		if (ctaId && /^[a-z0-9_]+(?:-footer)?$/.test(ctaId)) {
			cta.dataset.ctaId = ctaId;
		}
	});
}

function applyGuestCtas(scope = document) {
	scope.querySelectorAll(AUTH_CTA_SELECTOR).forEach((cta) => {
		if (!cta.dataset.guestLabel) cta.dataset.guestLabel = cta.textContent.trim();
		if (!cta.dataset.guestHref) cta.dataset.guestHref = cta.getAttribute("href") || "";
		if (!cta.dataset.guestCtaId) cta.dataset.guestCtaId = cta.dataset.ctaId || "";
		cta.textContent = cta.dataset.guestLabel;
		cta.setAttribute("href", cta.dataset.guestHref);
		if (cta.dataset.guestCtaId) cta.dataset.ctaId = cta.dataset.guestCtaId;
	});
}

function applyAccountState(scope, authenticated) {
	if (typeof authenticated !== "boolean") return;
	scope.querySelectorAll(ACCOUNT_CONTROL_SELECTOR).forEach((control) => {
		const pending = control.querySelector("[data-account-pending]");
		const guest = control.querySelector("[data-account-guest]");
		const account = control.querySelector("[data-account-auth]");
		if (pending) pending.hidden = true;
		if (guest) guest.hidden = authenticated;
		if (account) {
			account.hidden = !authenticated;
			if (!authenticated && account.matches("details")) account.removeAttribute("open");
		}
		control.dataset.accountState = accountViewState(authenticated);
	});
}

function setAccountStatus(control, message, state = "") {
	const status = control.querySelector("[data-account-status]");
	if (!status) return;
	status.textContent = message || "";
	status.dataset.state = state;
}

function initAccountControls(scope = document) {
	scope.querySelectorAll(ACCOUNT_CONTROL_SELECTOR).forEach((control) => {
		if (control.dataset.accountBound === "1") return;
		control.dataset.accountBound = "1";
		const button = control.querySelector("[data-account-logout]");
		if (!button) return;
		const defaultLabel = button.textContent;
		button.addEventListener("click", async () => {
			if (button.dataset.pending === "1") return;
			button.dataset.pending = "1";
			button.disabled = true;
			button.setAttribute("aria-busy", "true");
			button.textContent = button.dataset.labelPending;
			setAccountStatus(control, "");
			try {
				await logout();
				window.location.reload();
			} catch (error) {
				const status = control.querySelector("[data-account-status]");
				setAccountStatus(control, status?.dataset.error || "", "error");
				delete button.dataset.pending;
				button.disabled = false;
				button.removeAttribute("aria-busy");
				button.textContent = defaultLabel;
			}
		});
	});
}

function applySessionState(scope, state) {
	if (typeof state?.authenticated !== "boolean") return state;
	const authenticated = state.authenticated;
	const site = scope.querySelector?.(".vs-site") || document.querySelector(".vs-site");
	if (site) {
		site.dataset.sessionState = authenticated ? "authenticated" : "guest";
	}
	if (authenticated) applyAuthenticatedCtas(scope);
	else applyGuestCtas(scope);
	applyAccountState(scope, authenticated);
	updateBlogEngagementSession(scope, authenticated);
	return state;
}

async function refreshSessionUi(scope = document) {
	return applySessionState(scope, await getSessionState());
}

window.vlFleetThemePreference = {
	STORAGE_KEY: THEME_STORAGE_KEY,
	getIconName: getThemeIconName,
	normalizePreference: normalizeThemePreference,
	getSystemTheme,
	resolveTheme,
	getStoredPreference: getStoredThemePreference,
	setStoredPreference: setStoredThemePreference,
	applyThemeAttributes: applyThemePreference,
	initThemeControls,
};

window.vatiositeSessionCtas = {
	SESSION_STATE_URL,
	isAllowedFleetPath,
	applyAuthenticatedCtas,
	applyGuestCtas,
	applySessionState,
	getSessionState,
	refresh: refreshSessionUi,
};

window.vatiositeLanguagePreference = {
	getBrowserLanguages,
	getPreferredSupportedLanguage,
	init: initLanguagePreference,
	localizedUrl,
	normalizeLanguage,
	readStoredLanguage,
	shouldRedirectSavedSpanishHome,
	writeStoredLanguage,
};

const root = document.querySelector(".vs-site");

if (root) {
	initTheme();
	initLanguagePreference(root);
	initAutoplayVideos(root);
	initBlogEngagement(root);
	initAccountControls(root);
	refreshSessionUi(root);
	window.addEventListener("pageshow", (event) => {
		if (event.persisted) refreshSessionUi(root);
	});

	const metricsEnabled = root.dataset.metricsEnabled === "1";
	const pageId = root.dataset.pageId || "";
	const locale = root.dataset.locale || "en";

	const track = (event, ctaId = "") => {
		if (!metricsEnabled || !event || !pageId) return;
		const payload = JSON.stringify({
			event,
			page_id: pageId,
			locale,
			cta_id: ctaId || undefined,
			csrf_token: window.frappe?.csrf_token,
		});
		const url = "/api/method/vatiosite.analytics.track";
		if (navigator.sendBeacon) {
			navigator.sendBeacon(url, new Blob([payload], { type: "application/json" }));
			return;
		}
		fetch(url, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: payload,
			credentials: "same-origin",
			keepalive: true,
		}).catch(() => {});
	};

	track("page_view");

	root.addEventListener("click", (event) => {
		const target = event.target.closest("[data-event][data-cta-id]");
		if (!target) return;
		track(target.dataset.event, target.dataset.ctaId);
	});
}
